import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import { fetchArticles, fetchProducts, fetchCategories } from "../lib/api";
import ProductCard, { ProductLogo } from "../components/ProductCard";
import ArticleCard from "../components/ArticleCard";
import CategoryCard from "../components/CategoryCard";
import FeaturedEditorial from "../components/FeaturedEditorial";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchProducts({ featured: true }).then(setProducts).catch(() => {});
    Promise.all([fetchArticles({ featured: true, limit: 3 }), fetchArticles({ limit: 12 })])
      .then(([picks, latest]) => {
        const ids = new Set(picks.map((a) => a.id));
        setFeatured(picks);
        setArticles(latest.filter((a) => !ids.has(a.id)));
      })
      .catch(() => {});
    fetchCategories().then(setCategories).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-in-up">
            <p className="eyebrow mb-6" data-testid="hero-eyebrow">
              <Compass size={12} className="inline -mt-0.5 mr-2" />
              Independent software research
            </p>
            <h1 className="font-playfair text-5xl sm:text-6xl lg:text-7xl text-neutral-900 tracking-tight leading-[1.02] mb-7">
              Find software{" "}
              <span className="italic text-indigo-800">worth using.</span>
            </h1>
            <p className="text-lg text-neutral-600 max-w-xl leading-relaxed mb-9">
              Independent reviews, comparisons, and practical guides to help
              you discover better software for your business.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/reviews"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-indigo-800 text-surface text-sm font-semibold hover:bg-indigo-900 transition-colors"
                data-testid="hero-primary-cta"
              >
                Explore Reviews <ArrowRight size={15} />
              </Link>
              <Link
                to="/categories"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-neutral-300 text-neutral-900 text-sm font-semibold hover:border-indigo-800 hover:text-indigo-800 transition-colors"
                data-testid="hero-secondary-cta"
              >
                Browse Categories
              </Link>
            </div>
            <p className="mt-10 text-xs text-neutral-500 tracking-wide">
              Research-driven · Practical recommendations · Built for modern
              businesses
            </p>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative bg-surface border border-neutral-200 rounded-2xl p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <span className="eyebrow">On our radar</span>
                <span className="w-2 h-2 rounded-full bg-indigo-800 animate-pulse" />
              </div>
              <ul className="divide-y divide-neutral-200">
                {products.map((p, i) => (
                  <li key={p.id} data-testid={`hero-shortlist-${p.slug}`}>
                    <Link
                      to={`/reviews/${p.slug}-review`}
                      className="group py-3.5 flex items-center gap-4 min-w-0"
                    >
                      <span className="font-playfair italic text-neutral-400 w-6 shrink-0">
                        0{i + 1}
                      </span>
                      <ProductLogo product={p} size="sm" />
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium text-neutral-900 group-hover:text-indigo-900 transition-colors">{p.name}</span>
                        <span className="block text-xs text-neutral-500 truncate">{p.category}</span>
                      </span>
                      <ArrowRight size={14} className="text-neutral-400 group-hover:text-indigo-800 group-hover:translate-x-0.5 transition-[transform,color]" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="absolute -z-10 -top-6 -right-6 w-40 h-40 rounded-full bg-indigo-100/70 blur-3xl" />
            <div className="absolute -z-10 -bottom-8 -left-6 w-48 h-48 rounded-full bg-indigo-200/40 blur-3xl" />
          </div>
        </div>
      </section>

      {/* FEATURED EDITORIAL */}
      <FeaturedEditorial articles={featured} />

      {/* FEATURED SOFTWARE */}
      <section className="bg-section/70 border-y border-line" data-testid="featured-software">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="eyebrow mb-3">Featured</p>
            <h2 className="font-playfair text-3xl sm:text-4xl text-neutral-900 tracking-tight max-w-xl leading-tight">
              Software we're currently exploring
            </h2>
          </div>
          <Link
            to="/reviews"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-indigo-800 hover:text-indigo-950"
            data-testid="featured-view-all"
          >
            View all reviews <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
          {products.length % 3 !== 0 && (
            <Link
              to="/contact"
              data-testid="suggest-software-card"
              className="group rounded-xl border border-dashed border-indigo-200 bg-indigo-50/40 p-7 flex flex-col justify-between hover:bg-indigo-50 hover:border-indigo-300 transition-colors min-h-[220px]"
            >
              <p className="eyebrow">Suggest a product</p>
              <div>
                <h3 className="font-playfair text-2xl text-neutral-900 mb-2">Know a tool worth a closer look?</h3>
                <p className="text-sm text-neutral-600 leading-relaxed mb-5">Tell our editors which software you'd like us to research next.</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-800">
                  Suggest software <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </Link>
          )}
        </div>
      </div>
      </section>

      {/* LATEST ARTICLES */}
      <section>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="latest-articles">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow mb-3">Latest</p>
              <h2 className="font-playfair text-3xl sm:text-4xl text-neutral-900 tracking-tight max-w-xl leading-tight">
                Fresh reviews and practical guides
              </h2>
            </div>
            <Link
              to="/guides"
              className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-indigo-800 hover:text-indigo-950"
            >
              All articles <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {articles.slice(0, 6).map((a, i) => (
              <ArticleCard
                key={a.id}
                article={a}
                basePath={a.type === "review" ? "/reviews" : "/guides"}
                index={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="bg-section/45 border-t border-line">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24" data-testid="categories-section">
        <div className="mb-12">
          <p className="eyebrow mb-3">Browse by category</p>
          <h2 className="font-playfair text-3xl sm:text-4xl text-neutral-900 tracking-tight max-w-xl leading-tight">
            Where modern businesses spend their software budget
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((c) => (
            <CategoryCard key={c.id} category={c} />
          ))}
        </div>
      </div>
      </section>

      {/* EDITORIAL PHILOSOPHY */}
      <section className="bg-neutral-900 text-neutral-100">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <p className="eyebrow text-indigo-300 mb-6">Our philosophy</p>
          <p className="font-playfair text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-white">
            Useful first.{" "}
            <span className="italic text-indigo-300">Promotional second.</span>
          </p>
          <p className="mt-6 text-lg text-neutral-300 max-w-2xl leading-relaxed">
            We research software platforms, compare their capabilities,
            explore real-world use cases, and publish practical guides
            designed to help businesses choose tools that fit their needs.
          </p>
          <Link
            to="/about"
            className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-surface text-indigo-900 text-sm font-semibold hover:bg-indigo-50 transition-colors"
            data-testid="about-cta"
          >
            Read more about SaaSRadarHub <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
